import React, { useEffect, useMemo, useState } from "react";
import { dataService } from "./data.js";
import { money, moneyC, int, monthLabel, titleCase } from "./format.js";
import { Delta, TrendChart, MixStack, Skel, ErrorPanel, ChevronDown, CatTag, BizLink, PlaceLink } from "./components.jsx";
import { cityPath, countyPath } from "./slug.js";
import { countyCode } from "./tx-counties.js";
import { placeRating, namesDiffer } from "./places.js";
import { usePlan, FREE_HISTORY_MONTHS, downloadCsv } from "./plan.js";

/* Business profile - lazy fetch keyed by permit. */
export default function Profile({ permit, snap, onBack, onCity, onCounty, onOpen, watchlist, onToggleWatch, months, period, onPeriod, onUpgrade }) {
  const plan = usePlan();
  const [prof, setProf] = useState(null);
  const [scope, setScope] = useState(null);   // county-scoped snapshot for position + comparables
  const [gplace, setGplace] = useState(null); // Google rating (optional; null until/unless it loads)
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setProf(null); setError(null);
    dataService.business(permit)
      .then((d) => live && setProf(d))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [permit]);

  /* Optional Google rating - quietly no-ops without a key or if the API errs. */
  useEffect(() => {
    let live = true;
    setGplace(null);
    if (!prof) return;
    placeRating(prof.business)
      .then((d) => { if (live) setGplace(d); })
      .catch(() => { if (live) setGplace(null); });
    return () => { live = false; };
  }, [prof]);

  /* Market position and comparables are county-local, so pull a county-scoped
   * snapshot rather than reading them off the statewide overview snapshot,
   * which under-covers smaller cities and leaves these sections empty. */
  useEffect(() => {
    if (!prof || !period) { setScope(null); return; }
    let live = true;
    const b = prof.business;
    const code = countyCode(b.county);
    const q = code != null ? { county: code, limit: 600 } : { city: b.city, limit: 400 };
    dataService.snapshot(period, q)
      .then((d) => { if (live) setScope(d); })
      .catch(() => { if (live) setScope(null); });
    return () => { live = false; };
  }, [prof, period]);

  /* Prefer the county-scoped snapshot; fall back to the statewide one until it
   * lands (or if the scoped query fails). */
  const posSnap = scope || snap;

  const position = useMemo(() => {
    if (!prof || !posSnap) return null;
    const b = prof.business;
    const inCity = posSnap.businesses.filter((x) => x.city?.toUpperCase() === b.city?.toUpperCase());
    const inCounty = posSnap.businesses.filter((x) => x.county === b.county);
    const avg = (list) => (list.length ? Math.round(list.reduce((s, x) => s + x.total, 0) / list.length) : 0);
    const competitors = inCity.filter((x) => x.permit !== b.permit);
    const topComp = competitors[0] || null;
    const rank = inCounty.findIndex((x) => x.permit === b.permit) + 1 || null;
    return { cityAvg: avg(inCity), countyAvg: avg(inCounty), topComp, rank, countyCount: inCounty.length };
  }, [prof, posSnap]);

  const comparables = useMemo(() => {
    if (!prof || !posSnap) return [];
    const b = prof.business;
    const near = posSnap.businesses.filter((x) => x.permit !== b.permit &&
      (x.city?.toUpperCase() === b.city?.toUpperCase() || x.county === b.county));
    const sameCat = near.filter((x) => x.cat === b.cat);
    return [...sameCat, ...near.filter((x) => !sameCat.includes(x))].slice(0, 4);
  }, [prof, posSnap]);

  /* Free plan sees the trailing 12 months; Pro sees the full filing history. */
  const visibleHist = useMemo(() => {
    if (!prof) return [];
    return plan === "pro" ? prof.historical : prof.historical.slice(0, FREE_HISTORY_MONTHS);
  }, [prof, plan]);

  /* The hero (Total Reported Sales + deltas + mix) reflects the SELECTED
   * period, resolved from this permit's visible history. Falls back to the
   * latest filing when the chosen period is outside the shown window. */
  const active = useMemo(() => {
    if (!prof || !visibleHist.length) return null;
    const hist = visibleHist;                // reversed: latest month first
    let i = hist.findIndex((h) => h.month === period);
    if (i < 0) i = 0;
    const h = hist[i];
    const older = hist[i + 1];               // next entry is the prior month
    const mom = older
      ? (older.total > 0 ? +(((h.total - older.total) / older.total) * 100).toFixed(1) : null)
      : prof.business.chg;
    const mix = h.total > 0
      ? { l: h.liquor / h.total, w: h.wine / h.total, b: h.beer / h.total, c: h.cover / h.total }
      : { l: 0, w: 0, b: 0, c: 0 };
    return { month: h.month, total: h.total, mom, yoy: h.yoy, mix };
  }, [prof, period, visibleHist]);

  const crumbs = prof && (
    <div className="crumbs-inline">
      <button className="quiet-btn" style={{ padding: "0 2px" }} onClick={onBack}>Home</button>
      <span className="sep">›</span>
      <PlaceLink className="quiet-btn" style={{ padding: "0 2px" }} href={countyPath(prof.business.county)} onNav={() => onCounty(prof.business.county)}>{prof.business.county} County</PlaceLink>
      <span className="sep">›</span>
      <PlaceLink className="quiet-btn" style={{ padding: "0 2px" }} href={cityPath(prof.business.county, prof.business.city)} onNav={() => onCity(prof.business.city, prof.business.county)}>{titleCase(prof.business.city)}</PlaceLink>
      <span className="sep">›</span>
      <span className="here">{titleCase(prof.business.name)}</span>
    </div>
  );

  if (error) {
    return (
      <div className="container">
        <div className="crumbs-inline"><button className="quiet-btn" onClick={onBack}>← Overview</button></div>
        <ErrorPanel message={error} onRetry={() => { setError(null); dataService.business(permit).then(setProf).catch((e) => setError(e.message)); }} />
      </div>
    );
  }

  if (!prof) {
    return (
      <div className="container" aria-busy="true">
        <Skel w={260} h={13} style={{ marginBottom: 26 }} />
        <Skel w={140} h={22} style={{ marginBottom: 14 }} />
        <Skel w={420} h={34} style={{ marginBottom: 12 }} />
        <Skel w={340} h={13} style={{ marginBottom: 30 }} />
        <Skel w={280} h={54} style={{ marginBottom: 30 }} />
        <div className="panel panel-pad"><Skel w="100%" h={230} /></div>
      </div>
    );
  }

  const b = prof.business;
  const watched = watchlist.has(b.permit);
  const latestHist = prof.historical[0];

  return (
    <div className="container">
      {crumbs}

      <div className="title-row">
        <div className="title-main">
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <CatTag cat={b.cat} />
            <span className="tracked-flag"><span className="dot" aria-hidden="true" /> TRACKED</span>
          </div>
          <h1 className="profile-name">{titleCase(b.name)}</h1>
          <div className="profile-meta">
            <span>{titleCase(b.addr)}</span>
            <span>{titleCase(b.city)}, {b.county} County</span>
            <span className="permit">TABC {b.permit}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
          <button className={`btn ${watched ? "following" : ""}`} onClick={() => onToggleWatch(b.permit)}>
            {watched ? "✓ Following" : "+ Watchlist"}
          </button>
        </div>
      </div>

      <div className="hero-band">
        <div className="hero-callout">
          <div className="eyebrow">Total Reported Sales · {monthLabel(active.month).toUpperCase()}</div>
          <div className="big">{moneyC(active.total)}</div>
        </div>
        <div className="hero-deltas">
          <div className="hero-delta-cell">
            <span className="eyebrow">Month over month</span>
            <Delta v={active.mom} />
          </div>
          <div className="hero-delta-cell">
            <span className="eyebrow">Year over year</span>
            <Delta v={active.yoy} />
          </div>
          <div className="hero-delta-cell period-btn" style={{ border: "none", borderLeft: "1px solid var(--hairline)", borderRadius: 0 }}>
            <span className="eyebrow">Period</span>
            <span className="val" style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>{monthLabel(active.month)} <ChevronDown /></span>
            <select aria-label="Reporting period" value={active.month} onChange={(e) => onPeriod(e.target.value)}>
              {visibleHist.map((h) => (
                <option key={h.month} value={h.month}>{monthLabel(h.month)}{h.month === months?.latest ? " · partial" : ""}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="section two-col-1060">
        <div className="panel panel-pad">
          <div className="section-head" style={{ marginBottom: 6 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Monthly Sales Trend</h2>
              <div className="section-sub">Total reported mixed-beverage sales · hover for monthly values</div>
            </div>
            <span className="eyebrow right">Trailing 12 months</span>
          </div>
          <TrendChart data={prof.trend} height={252} big />
        </div>
        <div className="panel panel-pad">
          <div className="section-head" style={{ marginBottom: 4 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Sales Mix</h2>
              <div className="section-sub">Receipt composition · {monthLabel(active.month)}</div>
            </div>
          </div>
          <MixStack mix={active.mix} total={active.total} />
        </div>
      </div>

      {position && (
        <div className="section panel panel-pad">
          <div className="section-head" style={{ marginBottom: 18 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Market Position</h2>
              <div className="section-sub">How this location compares within its city, county, and against its nearest competitor</div>
            </div>
            <span className="eyebrow right">{monthLabel(active.month)}</span>
          </div>
          <div className="two-col-760">
            <div>
              <PosRow label="This business" val={active.total} max={maxOf(active.total, position)} subject />
              <PosRow label={`${titleCase(b.city)} average`} val={position.cityAvg} max={maxOf(active.total, position)} />
              <PosRow label={`${b.county} County average`} val={position.countyAvg} max={maxOf(active.total, position)} />
              {position.topComp && (
                <PosRow label="Top competitor" note={`· ${titleCase(position.topComp.name)}`} val={position.topComp.total} max={maxOf(active.total, position)} />
              )}
            </div>
            <div className="pos-narr">
              <p>
                <b style={{ color: "var(--ink)" }}>{titleCase(b.name)}</b> reported sales{" "}
                {active.total >= position.cityAvg ? "above" : "below"} the {titleCase(b.city)} tracked average
                for {monthLabel(active.month)}{active.mom != null ? <>, with month-over-month {active.mom >= 0 ? "growth" : "contraction"} of {Math.abs(active.mom).toFixed(1)}%</> : null}.
              </p>
              <p>
                {position.rank
                  ? <>Ranks {ordinal(position.rank)} among {position.countyCount} tracked venues in {b.county} County by reported receipts. </>
                  : null}
                Comparisons use tracked locations only; production data will incorporate the full permit universe.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="section">
        <div className="section-head">
          <div>
            <h2 className="section-h2">Historical Monthly Sales</h2>
            <div className="section-sub">
              {plan === "pro"
                ? `Full filing history · ${visibleHist.length} months with year-over-year change`
                : "Twelve months of reported receipts with year-over-year change"}
            </div>
          </div>
          <button
            className="quiet-btn right"
            onClick={() => {
              if (plan !== "pro") { onUpgrade(); return; }
              downloadCsv(
                `${titleCase(b.name).replace(/[^A-Za-z0-9]+/g, "-")}-history.csv`,
                ["Month", "Liquor", "Wine", "Beer", "Cover", "Total", "YoY %"],
                visibleHist.map((h) => [h.month, h.liquor, h.wine, h.beer, h.cover, h.total, h.yoy ?? ""])
              );
            }}
          >
            ⤓ Export CSV{plan !== "pro" && <span className="pro-tag">PRO</span>}
          </button>
        </div>
        <div className="table-scroll">
          <table className="data" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>Month</th>
                <th className="num col-liquor">Liquor</th>
                <th className="num col-wine">Wine</th>
                <th className="num col-beer">Beer</th>
                <th className="num col-cover">Cover</th>
                <th className="num">Total Sales</th>
                <th className="num">YoY</th>
              </tr>
            </thead>
            <tbody>
              {visibleHist.map((h) => (
                <tr key={h.month}>
                  <td style={{ fontWeight: 600, color: "var(--ink)" }}>{monthLabel(h.month, true)}</td>
                  <td className="num">{moneyC(h.liquor)}</td>
                  <td className="num">{moneyC(h.wine)}</td>
                  <td className="num">{moneyC(h.beer)}</td>
                  <td className="num">{moneyC(h.cover)}</td>
                  <td className="num strong">{moneyC(h.total)}</td>
                  <td className="num"><Delta v={h.yoy} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile fallback: the table above is hidden below 680px. */}
        <div className="stack-cards">
          {visibleHist.map((h) => (
            <div key={h.month} className="stack-card" style={{ cursor: "default" }}>
              <div className="stack-head">
                <span>
                  <span className="b-name" style={{ fontWeight: 700, color: "var(--ink)" }}>{monthLabel(h.month, true)}</span>
                  <div className="b-sub" style={{ fontSize: 12, color: "var(--muted-2)", marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
                    YoY <Delta v={h.yoy} />
                  </div>
                </span>
                <span style={{ textAlign: "right" }}>
                  <div className="num" style={{ fontWeight: 700, color: "var(--ink)" }}>{moneyC(h.total)}</div>
                  <div className="k" style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "1.2px", textTransform: "uppercase", color: "var(--faint-2)" }}>Total</div>
                </span>
              </div>
              <div className="stack-vals">
                {[["Liquor", "liquor", "var(--c-liquor)"], ["Wine", "wine", "var(--c-wine)"], ["Beer", "beer", "var(--c-beer-text)"], ["Cover", "cover", "var(--c-cover-text)"]].map(([lab, key, col]) => (
                  <div className="stack-val" key={key}>
                    <div className="k" style={{ color: col }}>{lab}</div>
                    <div className="v">{moneyC(h[key])}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {plan !== "pro" && prof.historical.length > visibleHist.length && (
          <div className="method-panel" style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <span>
              Showing the last {visibleHist.length} months, but this permit has{" "}
              <b>{prof.historical.length} months of filings</b> on record.
            </span>
            <button className="btn" onClick={onUpgrade}>Unlock full history · Pro</button>
          </div>
        )}
      </div>

      <div className="section">
        <div className="section-head">
          <div>
            <h2 className="section-h2">Comparable Locations</h2>
            <div className="section-sub">Nearby venues in {titleCase(b.city)} and {b.county} County · click to open a profile</div>
          </div>
        </div>
        {comparables.length ? (
          <div className="two-col-920">
            {comparables.map((c) => (
              <BizLink key={c.id} b={c} onOpen={onOpen} className="comp-card">
                <span>
                  <div className="comp-name">{titleCase(c.name)}</div>
                  <div className="comp-sub">{titleCase(c.city)} · {c.cat}</div>
                </span>
                <span className="comp-right">
                  <div className="comp-val">{money(c.total)}</div>
                  <Delta v={c.chg} />
                </span>
              </BizLink>
            ))}
          </div>
        ) : (
          <div className="method-panel">No other tracked venues in {titleCase(b.city)} or {b.county} County in the loaded snapshot.</div>
        )}
      </div>

      <div className="section two-col-1060">
        <div className="panel" style={{ overflow: "hidden" }}>
          <div className="section-head" style={{ padding: "20px 22px 0", marginBottom: 0 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Location</h2>
              <div className="section-sub">{titleCase(b.addr)}, {titleCase(b.city)}, TX {b.zip}</div>
            </div>
          </div>
          {/* Keyless Google embed pinned to the filed address; loads lazily so it
           * costs nothing until scrolled into view. */}
          <iframe
            className="biz-map"
            title={`Map of ${titleCase(b.name)}`}
            src={`https://maps.google.com/maps?q=${mapQuery(b)}&z=15&output=embed`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
        <div className="panel panel-pad">
          <div className="section-head" style={{ marginBottom: 12 }}>
            <div>
              <h2 className="section-h2" style={{ fontSize: 18 }}>Reviews &amp; Links</h2>
              <div className="section-sub">Jump straight to this venue on the review platforms</div>
            </div>
          </div>
          <a className="ext-row" href={gplace?.url || `https://www.google.com/maps/search/?api=1&query=${mapQuery(b)}`} target="_blank" rel="noopener noreferrer">
            <span>
              <div className="pulse-city">Google Maps &amp; Reviews</div>
              {gplace?.rating ? (
                <div className="pulse-sub">
                  <span className="gstars" aria-hidden="true">★</span> <b style={{ color: "var(--ink)" }}>{gplace.rating.toFixed(1)}</b>
                  {" "}· {int(gplace.count)} Google reviews
                  {namesDiffer(b.name, gplace.name) && (
                    <div style={{ marginTop: 3 }}>Listed on Google as <b style={{ color: "var(--ink)" }}>{gplace.name}</b> at this address</div>
                  )}
                </div>
              ) : (
                <div className="pulse-sub">Ratings, photos, hours, directions</div>
              )}
            </span>
            <span className="ext-arrow" aria-hidden="true">↗</span>
          </a>
          <a className="ext-row" href={`https://www.yelp.com/search?find_desc=${encodeURIComponent(gplace?.name || titleCase(b.name))}&find_loc=${encodeURIComponent(`${titleCase(b.addr)}, ${titleCase(b.city)}, TX`)}`} target="_blank" rel="noopener noreferrer">
            <span>
              <div className="pulse-city">Yelp</div>
              <div className="pulse-sub">Reviews and photos on Yelp</div>
            </span>
            <span className="ext-arrow" aria-hidden="true">↗</span>
          </a>
          <a className="ext-row" href={`https://www.tripadvisor.com/Search?q=${encodeURIComponent(`${gplace?.name || titleCase(b.name)} ${titleCase(b.city)} TX`)}`} target="_blank" rel="noopener noreferrer">
            <span>
              <div className="pulse-city">Tripadvisor</div>
              <div className="pulse-sub">Traveler reviews and rankings</div>
            </span>
            <span className="ext-arrow" aria-hidden="true">↗</span>
          </a>
          <div className="section-sub" style={{ marginTop: 14 }}>
            Ratings are hosted by the platforms above. Texas Bar Sales reports tax filings and does
            not collect or score reviews.
          </div>
        </div>
      </div>

      <div className="section method-panel">
        <b>Data source &amp; method.</b> Figures are gross mixed-beverage receipts as filed with the
        Texas Comptroller (liquor, wine, beer, and cover charge), grouped by TABC permit
        {latestHist ? `, latest filing ${monthLabel(latestHist.month)}` : ""}. The category label is
        inferred from the permit and name and is approximate. Month-over-month and year-over-year
        changes compare this permit’s own filings across periods.
      </div>
    </div>
  );
}

/* Address query for map/review deep-links, built from the filed address. */
const mapQuery = (b) => encodeURIComponent(`${b.name}, ${b.addr}, ${b.city}, TX ${b.zip || ""}`.trim());

const maxOf = (total, p) => Math.max(total, p.cityAvg, p.countyAvg, p.topComp?.total || 0, 1);
const ordinal = (n) => `${n}${["th", "st", "nd", "rd"][(n % 100 > 10 && n % 100 < 14) ? 0 : Math.min(n % 10, 4) % 4] || "th"}`;

function PosRow({ label, note, val, max, subject }) {
  return (
    <div className="pos-row">
      <div className="pos-head">
        <span className="pos-label">{label} {note && <span className="note">{note}</span>}</span>
        <span className={`pos-val ${subject ? "" : "plain"}`}>{moneyC(val)}</span>
      </div>
      <div className="pos-track">
        <div className={`pos-bar ${subject ? "subject" : ""}`} style={{ width: `${(val / max) * 100}%` }} />
      </div>
    </div>
  );
}
